import { Badge, Combobox, Group, InputBase, Text, ThemeIcon, useCombobox } from "@mantine/core";
import { useRef, useState } from "react";

import classes from "./ChartFilterDropdown.module.css";

import { DropdownChevron } from "@/components/elements/Dropdown/components";
import {
  getPropsFromComboboxItem,
  openedInputStyle,
  useDropdownPosition,
} from "@/components/elements/Dropdown/utils";
import { IconCheck } from "@/components/icons";

import type { FC } from "react";

export type ChartFilterDropdownProps = {
  data: { label: string; value: string }[];
  label: string;
  onChange: (values: string[]) => void;
  searchPlaceholder?: string;
  searchable?: boolean;
  values: string[];
};

export const ChartFilterDropdown: FC<ChartFilterDropdownProps> = ({
  data,
  label,
  onChange,
  searchPlaceholder,
  searchable = false,
  values,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [opened, setOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const position = useDropdownPosition(ref);
  const combobox = useCombobox({
    onDropdownOpen: () => setOpened(true),
    onDropdownClose: () => {
      setOpened(false);
      setSearchQuery("");
    },
    opened,
  });
  const filteredData =
    searchable && searchQuery
      ? data.filter((item) => item.label.toLowerCase().includes(searchQuery.toLowerCase()))
      : data;
  const inputStyle = position ? openedInputStyle[position] : undefined;

  function isActive(value: string) {
    return values.includes(value);
  }

  function handleOptionSubmit(value: string) {
    onChange(isActive(value) ? values.filter((item) => item !== value) : [...values, value]);
  }

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSubmit} withinPortal>
      <Combobox.Target>
        <InputBase
          component="button"
          miw={150}
          onClick={() => combobox.toggleDropdown()}
          pointer
          rightSection={
            <DropdownChevron opened={opened} size="sm" onClick={() => combobox.toggleDropdown()} />
          }
          rightSectionPointerEvents="none"
          size="sm"
          styles={{ input: opened ? inputStyle : undefined }}
          type="button"
        >
          <Group gap="xs" wrap="nowrap">
            {values.length > 0 && (
              <Badge circle size="sm" variant="filled">
                {values.length}
              </Badge>
            )}
            <Text size="sm" truncate>
              {label}
            </Text>
          </Group>
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown ref={ref} classNames={{ dropdown: classes.dropdown }}>
        {searchable && (
          <Combobox.Search
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
            placeholder={searchPlaceholder}
            value={searchQuery}
          />
        )}
        <Combobox.Options>
          {filteredData.map((item) => {
            const { label: optionLabel, value } = getPropsFromComboboxItem(item);
            const active = isActive(value);

            return (
              <Combobox.Option key={value} active={active} value={value}>
                <Group className={classes.option} gap="sm" wrap="nowrap">
                  <Text className={classes.optionLabel} size="sm">
                    {optionLabel}
                  </Text>
                  {active && (
                    <ThemeIcon
                      className={classes.checkIcon}
                      color="secondary"
                      size="xs"
                      variant="transparent"
                    >
                      <IconCheck size={16} />
                    </ThemeIcon>
                  )}
                </Group>
              </Combobox.Option>
            );
          })}
          {filteredData.length === 0 && <Combobox.Empty>{label}</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
